/**
 * CaseFileCreationForm - Shared component for creating CaseFiles
 *
 * Used by both Onboarding.tsx (first-time users) and CreateCaseFile.tsx (existing users).
 * Handles authority type detection, parental attestation for minors, and all
 * ACL v2.3 authorization flows.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { CaseFileAuthority } from '../../types/settings';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Parental attestation data for parent_of_minor authority type
 */
export interface ParentalAttestation {
  attestedAt: string;
  attestationText: string;
}

/**
 * Legal guardian attestation for legal_guardian authority type
 */
export interface GuardianshipAttestation {
  attestedAt: string;
  attestationText: string;
  documentPending: boolean;
}

/**
 * Authorization method for designated agent flow
 */
export type DesignatedAgentAuthMethod = 'in_app' | 'upload' | 'email';

/**
 * Designated agent data for designated_agent authority type
 */
export interface DesignatedAgentData {
  careRecipientEmail: string;
  relationshipToPatient: string;
  attestationText: string;
  authorizationMethod: DesignatedAgentAuthMethod;
}

/**
 * Insurance information
 */
export interface InsuranceFormData {
  provider: string;
  policyNumber: string;
  groupNumber?: string;
}

/**
 * Complete form data returned on submit
 */
export interface CaseFileFormData {
  name: string;
  dateOfBirth: string;
  diagnoses?: string[];
  insuranceInfo?: InsuranceFormData;
  authorityType: CaseFileAuthority;
  ownerName: string;
  ownerRelationship: string;
  parentalAttestation?: ParentalAttestation;
  guardianshipAttestation?: GuardianshipAttestation;
  designatedAgentData?: DesignatedAgentData;
}

/**
 * Component props
 */
export interface CaseFileCreationFormProps {
  onSubmit: (data: CaseFileFormData) => Promise<void>;
  isLoading?: boolean;
  showInsurance?: boolean;
  submitButtonText?: string;
  showBackButton?: boolean;
  onBack?: () => void;
  backButtonText?: string;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Calculate age from date of birth
 */
function calculateAge(dob: string): number {
  const today = new Date();
  const birthDate = new Date(dob + 'T00:00:00Z');
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

/**
 * Determine if care recipient is a minor (under 18)
 */
function isMinorAge(dob: string): boolean {
  if (!dob) return false;
  return calculateAge(dob) < 18;
}

/**
 * Generate parental attestation text for minors
 */
function generateParentalAttestationText(childName: string, dob: string): string {
  return `I attest that I am the parent or legal guardian of ${childName} (born ${dob}) and have the legal authority to manage their healthcare information.`;
}

/**
 * Generate guardianship attestation text
 */
function generateGuardianshipAttestationText(wardName: string): string {
  return `I attest that I am the court-appointed legal guardian of ${wardName} and have the legal authority to manage their healthcare information. I understand that I must upload guardianship documentation to verify my authority.`;
}

/**
 * Generate designated agent attestation text
 */
function generateDesignatedAgentAttestationText(patientName: string): string {
  return `I understand that ${patientName} must authorize me to manage their healthcare information, and this CaseFile will remain locked until the authorization process is complete.`;
}

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Authority type options for adults (when they are NOT the care recipient)
 */
const ADULT_NON_SELF_OPTIONS: { value: CaseFileAuthority; label: string; description: string }[] = [
  {
    value: 'designated_agent',
    label: 'They will authorize me',
    description: 'The care recipient is a competent adult who will grant you access via email authorization.',
  },
  {
    value: 'legal_guardian',
    label: 'I am their legal guardian',
    description: 'You have been appointed by a court as the legal guardian. Documentation required.',
  },
];

/**
 * Relationship options for designated agents
 */
const AGENT_RELATIONSHIP_OPTIONS: { value: string; label: string }[] = [
  { value: 'spouse', label: 'Spouse/Partner' },
  { value: 'child', label: 'Adult Child' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'parent', label: 'Parent' },
  { value: 'grandparent', label: 'Grandparent' },
  { value: 'extended', label: 'Extended Family' },
  { value: 'other', label: 'Other (Friend, Neighbor, etc.)' },
];

// =============================================================================
// COMPONENT
// =============================================================================

export function CaseFileCreationForm({
  onSubmit,
  isLoading = false,
  showInsurance = true,
  submitButtonText = 'Create CaseFile',
  showBackButton = false,
  onBack,
  backButtonText = 'Back',
}: CaseFileCreationFormProps): JSX.Element {
  const { user } = useAuth();

  // Basic form state
  const [name, setName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [diagnoses, setDiagnoses] = useState('');
  const [insuranceProvider, setInsuranceProvider] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [groupNumber, setGroupNumber] = useState('');

  // Authority type state
  const [isMinor, setIsMinor] = useState(false);
  const [isSelf, setIsSelf] = useState<boolean | null>(null); // null = not answered yet
  const [authorityType, setAuthorityType] = useState<CaseFileAuthority>('patient_self');

  // Attestation state
  const [parentalAttestationChecked, setParentalAttestationChecked] = useState(false);
  const [guardianshipAttestationChecked, setGuardianshipAttestationChecked] = useState(false);
  const [agentAttestationChecked, setAgentAttestationChecked] = useState(false);

  // Designated agent state
  const [careRecipientEmail, setCareRecipientEmail] = useState('');
  const [agentRelationship, setAgentRelationship] = useState('spouse');
  const [authorizationMethod, setAuthorizationMethod] = useState<DesignatedAgentAuthMethod>('in_app');

  // Validation state
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);

  // =============================================================================
  // EFFECTS
  // =============================================================================

  // Detect if minor when DOB changes
  useEffect(() => {
    if (dateOfBirth) {
      const minor = isMinorAge(dateOfBirth);
      setIsMinor(minor);

      if (minor) {
        // For minors, always use parent_of_minor
        setAuthorityType('parent_of_minor');
        setIsSelf(null); // Reset the "is self" question
      } else {
        // For adults, reset to patient_self if coming from minor
        if (authorityType === 'parent_of_minor') {
          setAuthorityType('patient_self');
          setIsSelf(null);
        }
      }

      // Reset attestations when DOB changes
      setParentalAttestationChecked(false);
      setGuardianshipAttestationChecked(false);
      setAgentAttestationChecked(false);
    }
  }, [dateOfBirth]);

  // Update authority type when "is self" answer changes (for adults)
  useEffect(() => {
    if (!isMinor && isSelf === true) {
      setAuthorityType('patient_self');
    } else if (!isMinor && isSelf === false) {
      // Default to designated_agent when not self
      if (authorityType === 'patient_self') {
        setAuthorityType('designated_agent');
      }
    }
  }, [isSelf, isMinor]);

  // =============================================================================
  // VALIDATION
  // =============================================================================

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Name validation
    if (!name.trim()) {
      errors.name = 'Name is required';
    }

    // DOB validation
    if (!dateOfBirth) {
      errors.dateOfBirth = 'Date of birth is required';
    } else {
      const date = new Date(dateOfBirth + 'T00:00:00Z');
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (isNaN(date.getTime())) {
        errors.dateOfBirth = 'Invalid date';
      } else if (date > today) {
        errors.dateOfBirth = 'Date of birth cannot be in the future';
      }
    }

    // Insurance validation (if provider specified, policy number required)
    if (insuranceProvider.trim() && !policyNumber.trim()) {
      errors.policyNumber = 'Policy number is required when provider is specified';
    }

    // Authority-specific validation
    if (isMinor) {
      if (!parentalAttestationChecked) {
        errors.parentalAttestation = 'Parental attestation is required for minor care recipients';
      }
    } else if (isSelf === null && dateOfBirth && name.trim()) {
      errors.isSelf = 'Please indicate if this CaseFile is for yourself';
    } else if (isSelf === false) {
      if (authorityType === 'legal_guardian' && !guardianshipAttestationChecked) {
        errors.guardianshipAttestation = 'Guardianship attestation is required';
      } else if (authorityType === 'designated_agent') {
        // Email is only required when the email authorization method is selected
        if (authorizationMethod === 'email') {
          if (!careRecipientEmail.trim()) {
            errors.careRecipientEmail = "Care recipient's email is required for email authorization";
          } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(careRecipientEmail.trim())) {
            errors.careRecipientEmail = 'Please enter a valid email address';
          }
        }
        if (!agentAttestationChecked) {
          errors.agentAttestation = 'You must acknowledge the authorization process';
        }
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // =============================================================================
  // FORM SUBMISSION
  // =============================================================================

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGeneralError(null);

    if (!validateForm()) {
      return;
    }

    // Build diagnoses array
    const diagnosesArray = diagnoses
      .split(',')
      .map((d) => d.trim())
      .filter((d) => d.length > 0);

    // Build insurance info (only if provider specified)
    const hasInsurance = insuranceProvider.trim().length > 0;
    const insuranceData: InsuranceFormData | undefined = hasInsurance
      ? {
          provider: insuranceProvider.trim(),
          policyNumber: policyNumber.trim(),
          ...(groupNumber.trim() && { groupNumber: groupNumber.trim() }),
        }
      : undefined;

    // Build parental attestation
    const parentalAttestation: ParentalAttestation | undefined =
      authorityType === 'parent_of_minor' && parentalAttestationChecked
        ? {
            attestedAt: new Date().toISOString(),
            attestationText: generateParentalAttestationText(name.trim(), dateOfBirth),
          }
        : undefined;

    // Build guardianship attestation
    const guardianshipAttestation: GuardianshipAttestation | undefined =
      authorityType === 'legal_guardian' && guardianshipAttestationChecked
        ? {
            attestedAt: new Date().toISOString(),
            attestationText: generateGuardianshipAttestationText(name.trim()),
            documentPending: true,
          }
        : undefined;

    // Build designated agent data
    const designatedAgentData: DesignatedAgentData | undefined =
      authorityType === 'designated_agent'
        ? {
            careRecipientEmail: careRecipientEmail.trim(),
            relationshipToPatient: agentRelationship,
            attestationText: generateDesignatedAgentAttestationText(name.trim()),
            authorizationMethod: authorizationMethod,
          }
        : undefined;

    // Determine owner relationship based on authority type
    let ownerRelationship: string = 'self';
    if (authorityType === 'parent_of_minor') {
      ownerRelationship = 'parent';
    } else if (authorityType === 'legal_guardian') {
      ownerRelationship = 'other';
    } else if (authorityType === 'designated_agent') {
      ownerRelationship = agentRelationship;
    }

    // Build form data
    const formData: CaseFileFormData = {
      name: name.trim(),
      dateOfBirth: dateOfBirth,
      ...(diagnosesArray.length > 0 && { diagnoses: diagnosesArray }),
      ...(insuranceData && { insuranceInfo: insuranceData }),
      authorityType: authorityType,
      ownerName: user?.displayName || user?.email || '',
      ownerRelationship: ownerRelationship,
      ...(parentalAttestation && { parentalAttestation }),
      ...(guardianshipAttestation && { guardianshipAttestation }),
      ...(designatedAgentData && { designatedAgentData }),
    };

    try {
      await onSubmit(formData);
    } catch (err) {
      console.error('Error submitting form:', err);
      if (err instanceof Error) {
        setGeneralError(err.message || 'Failed to create CaseFile. Please try again.');
      } else {
        setGeneralError('Failed to create CaseFile. Please try again.');
      }
    }
  };

  // =============================================================================
  // RENDER HELPERS
  // =============================================================================

  const showIsSelfQuestion = !isMinor && dateOfBirth && name.trim();
  const showAuthorityOptions = !isMinor && isSelf === false;
  const showParentalAttestation = isMinor && dateOfBirth && name.trim();
  const showGuardianshipAttestation = authorityType === 'legal_guardian' && !isMinor && isSelf === false;
  const showDesignatedAgentSection = authorityType === 'designated_agent' && !isMinor && isSelf === false;

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Care Recipient Information */}
      <div className="space-y-4">
        <h3 className="font-semibold text-ink border-b border-soft-neutral pb-2">
          Care Recipient Information
        </h3>

        {/* Full Name */}
        <div>
          <label htmlFor="cf-name" className="label">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            id="cf-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`input ${fieldErrors.name ? 'input-error' : ''}`}
            placeholder="e.g., Alex Johnson"
            disabled={isLoading}
            aria-invalid={fieldErrors.name ? true : undefined}
            aria-describedby={fieldErrors.name ? 'cf-name-error' : undefined}
          />
          {fieldErrors.name && <p id="cf-name-error" className="error-text" role="alert">{fieldErrors.name}</p>}
        </div>

        {/* Date of Birth */}
        <div>
          <label htmlFor="cf-dob" className="label">
            Date of Birth <span className="text-red-500">*</span>
          </label>
          <input
            id="cf-dob"
            type="date"
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
            className={`input ${fieldErrors.dateOfBirth ? 'input-error' : ''}`}
            max={new Date().toISOString().split('T')[0]}
            disabled={isLoading}
            aria-invalid={fieldErrors.dateOfBirth ? true : undefined}
            aria-describedby={fieldErrors.dateOfBirth ? 'cf-dob-error' : undefined}
          />
          {fieldErrors.dateOfBirth && <p id="cf-dob-error" className="error-text" role="alert">{fieldErrors.dateOfBirth}</p>}
        </div>

        {/* Diagnoses */}
        <div>
          <label htmlFor="cf-diagnoses" className="label">
            Diagnoses
          </label>
          <input
            id="cf-diagnoses"
            type="text"
            value={diagnoses}
            onChange={(e) => setDiagnoses(e.target.value)}
            className="input"
            placeholder="e.g., Autism Spectrum Disorder, ADHD"
            disabled={isLoading}
          />
          <p className="helper-text">Separate multiple diagnoses with commas (optional)</p>
        </div>
      </div>

      {/* Is Self Question (for adults only) */}
      {showIsSelfQuestion && (
        <div className="space-y-4">
          <h3 className="font-semibold text-ink border-b border-soft-neutral pb-2">
            Is this CaseFile for yourself? <span className="text-red-500">*</span>
          </h3>
          <div className="space-y-3">
            <label
              className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                isSelf === true
                  ? 'border-evergreen bg-evergreen/5'
                  : 'border-soft-neutral hover:border-brass'
              }`}
            >
              <input
                type="radio"
                name="isSelf"
                checked={isSelf === true}
                onChange={() => setIsSelf(true)}
                className="mt-1 h-4 w-4 text-evergreen border-gray-300 focus:ring-evergreen"
                disabled={isLoading}
              />
              <div>
                <p className="font-medium text-ink">Yes, I am {name.trim()}</p>
                <p className="text-sm text-muted">
                  You are creating this CaseFile to manage your own healthcare information.
                </p>
              </div>
            </label>

            <label
              className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                isSelf === false
                  ? 'border-evergreen bg-evergreen/5'
                  : 'border-soft-neutral hover:border-brass'
              }`}
            >
              <input
                type="radio"
                name="isSelf"
                checked={isSelf === false}
                onChange={() => setIsSelf(false)}
                className="mt-1 h-4 w-4 text-evergreen border-gray-300 focus:ring-evergreen"
                disabled={isLoading}
              />
              <div>
                <p className="font-medium text-ink">No, I am caring for {name.trim()}</p>
                <p className="text-sm text-muted">
                  You are a caregiver creating this CaseFile on behalf of someone else.
                </p>
              </div>
            </label>
          </div>
          {fieldErrors.isSelf && <p className="error-text">{fieldErrors.isSelf}</p>}
        </div>
      )}

      {/* Authority Type Options (for non-self adults) */}
      {showAuthorityOptions && (
        <div className="space-y-4">
          <h3 className="font-semibold text-ink border-b border-soft-neutral pb-2">
            Your Authorization to Manage {name.trim()}'s Information
            <span className="text-red-500 ml-1">*</span>
          </h3>
          <div className="space-y-3">
            {ADULT_NON_SELF_OPTIONS.map((option) => (
              <label
                key={option.value}
                className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                  authorityType === option.value
                    ? 'border-evergreen bg-evergreen/5'
                    : 'border-soft-neutral hover:border-brass'
                }`}
              >
                <input
                  type="radio"
                  name="authorityType"
                  value={option.value}
                  checked={authorityType === option.value}
                  onChange={(e) => setAuthorityType(e.target.value as CaseFileAuthority)}
                  className="mt-1 h-4 w-4 text-evergreen border-gray-300 focus:ring-evergreen"
                  disabled={isLoading}
                />
                <div>
                  <p className="font-medium text-ink">{option.label}</p>
                  <p className="text-sm text-muted">{option.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Parental Attestation (for minors) */}
      {showParentalAttestation && (
        <div className="space-y-4">
          <h3 className="font-semibold text-ink border-b border-soft-neutral pb-2">
            Parental Authorization
            <span className="text-red-500 ml-1">*</span>
          </h3>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800 mb-3">
              The care recipient is under 18 years old. As the parent or legal guardian,
              you must attest that you have legal authority to manage their healthcare information.
            </p>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={parentalAttestationChecked}
                onChange={(e) => setParentalAttestationChecked(e.target.checked)}
                className="mt-1 h-4 w-4 text-evergreen border-gray-300 rounded focus:ring-evergreen"
                disabled={isLoading}
              />
              <span className="text-sm text-gray-700">
                {generateParentalAttestationText(name.trim(), dateOfBirth)}
              </span>
            </label>
            {fieldErrors.parentalAttestation && (
              <p className="error-text mt-2">{fieldErrors.parentalAttestation}</p>
            )}
          </div>
        </div>
      )}

      {/* Legal Guardian Attestation */}
      {showGuardianshipAttestation && (
        <div className="space-y-4">
          <h3 className="font-semibold text-ink border-b border-soft-neutral pb-2">
            Guardianship Verification
            <span className="text-red-500 ml-1">*</span>
          </h3>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800 mb-3">
              As a court-appointed legal guardian, you must provide documentation of your guardianship.
              You can upload this documentation after creating the CaseFile.
            </p>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={guardianshipAttestationChecked}
                onChange={(e) => setGuardianshipAttestationChecked(e.target.checked)}
                className="mt-1 h-4 w-4 text-evergreen border-gray-300 rounded focus:ring-evergreen"
                disabled={isLoading}
              />
              <span className="text-sm text-gray-700">
                {generateGuardianshipAttestationText(name.trim())}
              </span>
            </label>
            {fieldErrors.guardianshipAttestation && (
              <p className="error-text mt-2">{fieldErrors.guardianshipAttestation}</p>
            )}
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-600">
              <strong>Note:</strong> The CaseFile will be created, but you will need to upload
              guardianship documentation (court order, letters of guardianship, etc.) to complete
              the verification process.
            </p>
          </div>
        </div>
      )}

      {/* Designated Agent Authorization */}
      {showDesignatedAgentSection && (
        <div className="space-y-4">
          <h3 className="font-semibold text-ink border-b border-soft-neutral pb-2">
            Patient Authorization Required
            <span className="text-red-500 ml-1">*</span>
          </h3>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-4">
            <p className="text-sm text-purple-800">
              {name.trim()} must authorize you to manage their healthcare information.
              Choose how you'd like to complete authorization.
            </p>

            {/* Authorization Method Selection */}
            <div>
              <label className="label text-purple-900">
                How would you like to complete authorization? <span className="text-red-500">*</span>
              </label>
              <div className="space-y-3 mt-2">
                <label
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    authorizationMethod === 'in_app'
                      ? 'border-evergreen bg-evergreen/5'
                      : 'border-soft-neutral hover:border-brass'
                  }`}
                >
                  <input
                    type="radio"
                    name="authMethod"
                    value="in_app"
                    checked={authorizationMethod === 'in_app'}
                    onChange={() => setAuthorizationMethod('in_app')}
                    className="mt-1 h-4 w-4 text-evergreen border-gray-300 focus:ring-evergreen"
                    disabled={isLoading}
                  />
                  <div>
                    <p className="font-medium text-ink">In-App E-Signature</p>
                    <p className="text-sm text-muted">
                      Patient is here now and can sign on this device
                    </p>
                  </div>
                </label>

                <label
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    authorizationMethod === 'upload'
                      ? 'border-evergreen bg-evergreen/5'
                      : 'border-soft-neutral hover:border-brass'
                  }`}
                >
                  <input
                    type="radio"
                    name="authMethod"
                    value="upload"
                    checked={authorizationMethod === 'upload'}
                    onChange={() => setAuthorizationMethod('upload')}
                    className="mt-1 h-4 w-4 text-evergreen border-gray-300 focus:ring-evergreen"
                    disabled={isLoading}
                  />
                  <div>
                    <p className="font-medium text-ink">Upload Signed Document</p>
                    <p className="text-sm text-muted">
                      I have a paper authorization form that's already signed
                    </p>
                  </div>
                </label>

                <label
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    authorizationMethod === 'email'
                      ? 'border-evergreen bg-evergreen/5'
                      : 'border-soft-neutral hover:border-brass'
                  }`}
                >
                  <input
                    type="radio"
                    name="authMethod"
                    value="email"
                    checked={authorizationMethod === 'email'}
                    onChange={() => setAuthorizationMethod('email')}
                    className="mt-1 h-4 w-4 text-evergreen border-gray-300 focus:ring-evergreen"
                    disabled={isLoading}
                  />
                  <div>
                    <p className="font-medium text-ink">Send Email Request</p>
                    <p className="text-sm text-muted">
                      Patient will receive an email link to authorize remotely
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Care Recipient Email — only shown for email method */}
            {authorizationMethod === 'email' && (
              <div>
                <label htmlFor="cf-recipient-email" className="label text-purple-900">
                  {name.trim()}'s Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  id="cf-recipient-email"
                  type="email"
                  value={careRecipientEmail}
                  onChange={(e) => setCareRecipientEmail(e.target.value)}
                  className={`input ${fieldErrors.careRecipientEmail ? 'input-error' : ''}`}
                  placeholder="patient@example.com"
                  disabled={isLoading}
                  aria-invalid={fieldErrors.careRecipientEmail ? true : undefined}
                  aria-describedby={fieldErrors.careRecipientEmail ? 'cf-recipient-email-error' : undefined}
                />
                {fieldErrors.careRecipientEmail && (
                  <p id="cf-recipient-email-error" className="error-text" role="alert">{fieldErrors.careRecipientEmail}</p>
                )}
              </div>
            )}

            {/* Relationship to Patient */}
            <div>
              <label htmlFor="cf-agent-relationship" className="label text-purple-900">
                Your Relationship to {name.trim()}
              </label>
              <select
                id="cf-agent-relationship"
                value={agentRelationship}
                onChange={(e) => setAgentRelationship(e.target.value)}
                className="input"
                disabled={isLoading}
              >
                {AGENT_RELATIONSHIP_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Agent Attestation */}
            <label className="flex items-start gap-3 cursor-pointer pt-2 border-t border-purple-200">
              <input
                type="checkbox"
                checked={agentAttestationChecked}
                onChange={(e) => setAgentAttestationChecked(e.target.checked)}
                className="mt-1 h-4 w-4 text-evergreen border-gray-300 rounded focus:ring-evergreen"
                disabled={isLoading}
              />
              <span className="text-sm text-gray-700">
                {generateDesignatedAgentAttestationText(name.trim())}
              </span>
            </label>
            {fieldErrors.agentAttestation && (
              <p className="error-text">{fieldErrors.agentAttestation}</p>
            )}
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs text-amber-800">
              <strong>Important:</strong> The CaseFile will be created in a locked state until{' '}
              {name.trim()} completes the authorization process.{' '}
              {authorizationMethod === 'in_app'
                ? 'You will complete the e-signature process on the next screen.'
                : authorizationMethod === 'upload'
                ? 'You will upload the signed document on the next screen.'
                : 'You will be notified when they respond.'}
            </p>
          </div>
        </div>
      )}

      {/* Insurance Information (optional) */}
      {showInsurance && (
        <div className="space-y-4">
          <h3 className="font-semibold text-ink border-b border-soft-neutral pb-2">
            Insurance Information
            <span className="text-sm font-normal text-muted ml-2">(Optional)</span>
          </h3>
          <p className="text-sm text-muted -mt-2">
            You can add insurance details later in the CaseFile settings.
          </p>

          {/* Insurance Provider */}
          <div>
            <label htmlFor="cf-insurance-provider" className="label">
              Insurance Provider
            </label>
            <input
              id="cf-insurance-provider"
              type="text"
              value={insuranceProvider}
              onChange={(e) => setInsuranceProvider(e.target.value)}
              className="input"
              placeholder="e.g., Aetna, Blue Cross Blue Shield"
              disabled={isLoading}
            />
          </div>

          {/* Policy Number */}
          <div>
            <label htmlFor="cf-policy-number" className="label">
              Policy Number
            </label>
            <input
              id="cf-policy-number"
              type="text"
              value={policyNumber}
              onChange={(e) => setPolicyNumber(e.target.value)}
              className={`input ${fieldErrors.policyNumber ? 'input-error' : ''}`}
              placeholder="e.g., ABC123456"
              disabled={isLoading}
            />
            {fieldErrors.policyNumber && <p className="error-text">{fieldErrors.policyNumber}</p>}
            <p className="helper-text">Required if insurance provider is specified</p>
          </div>

          {/* Group Number */}
          <div>
            <label htmlFor="cf-group-number" className="label">
              Group Number
            </label>
            <input
              id="cf-group-number"
              type="text"
              value={groupNumber}
              onChange={(e) => setGroupNumber(e.target.value)}
              className="input"
              placeholder="e.g., GRP789"
              disabled={isLoading}
            />
          </div>
        </div>
      )}

      {/* Error Display */}
      {generalError && (
        <div className="alert alert-error">{generalError}</div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-4 pt-4">
        {showBackButton && onBack && (
          <button
            type="button"
            onClick={onBack}
            disabled={isLoading}
            className="btn-secondary"
          >
            {backButtonText}
          </button>
        )}
        <button
          type="submit"
          disabled={isLoading}
          className={`btn-primary ${showBackButton ? 'flex-1' : 'w-full'}`}
        >
          {isLoading ? 'Creating...' : submitButtonText}
        </button>
      </div>
    </form>
  );
}

export default CaseFileCreationForm;
